import { getStore, updateStore } from 'core_main/store';
import type { ChatSessionsData } from 'core_main/api/sessions/types';

const CUSTOMER_TOKEN = (customerId: string) => `cust-${customerId}`;

const replaceCustomerToken = (
  value: string | undefined,
  fromCustomerId: string,
  toCustomerId: string,
): string | undefined => {
  if (!value) return value;
  const fromToken = CUSTOMER_TOKEN(fromCustomerId);
  if (!value.includes(fromToken)) return value;
  return value.split(fromToken).join(CUSTOMER_TOKEN(toCustomerId));
};

/**
 * After a customer merge, `session.update` often lands in the store *before*
 * chat-api `recustomerPubnub` rewrites channel names on the server. The client
 * re-keys PubNub UUID/auth to the survivor, but typing / mark-read / history
 * still read stale `cust-<source>` channel names from `sessions` → PAM 403.
 *
 * Rewrite in-memory session PubNub channel names (and channel group) from the
 * pre-merge customer to the survivor so publish/signal/history match grants.
 */
export const rewriteSessionPubnubCustomerInStore = (
  fromCustomerId: string,
  toCustomerId: string,
): void => {
  if (!fromCustomerId || !toCustomerId || fromCustomerId === toCustomerId) {
    return;
  }

  const { sessions } = getStore();
  if (!sessions || Object.keys(sessions).length === 0) {
    return;
  }

  let changed = false;
  const nextSessions: Record<string, ChatSessionsData> = { ...sessions };

  Object.keys(sessions).forEach((sessionId) => {
    const session = sessions[sessionId];
    const pubnubData = session?.attributes?.pubnub?.data;
    if (!pubnubData) {
      return;
    }

    const nextChannelGroup = replaceCustomerToken(
      pubnubData.channelGroup,
      fromCustomerId,
      toCustomerId,
    );
    const nextChannels = (pubnubData.channels || []).map((channel) => {
      const nextName = replaceCustomerToken(
        channel?.name,
        fromCustomerId,
        toCustomerId,
      );
      if (!nextName || nextName === channel.name) {
        return channel;
      }
      return { ...channel, name: nextName };
    });

    const customerRelId = session.relationships?.customer?.data?.id;
    const needsCustomerRel = customerRelId === fromCustomerId;
    const needsChannelGroup =
      !!nextChannelGroup && nextChannelGroup !== pubnubData.channelGroup;
    const needsChannels = nextChannels.some(
      (channel, index) => channel !== pubnubData.channels[index],
    );

    if (!needsChannelGroup && !needsChannels && !needsCustomerRel) {
      return;
    }

    changed = true;
    nextSessions[sessionId] = {
      ...session,
      attributes: {
        ...session.attributes,
        pubnub: {
          ...session.attributes.pubnub,
          data: {
            ...pubnubData,
            ...(needsChannelGroup
              ? { channelGroup: nextChannelGroup as string }
              : {}),
            ...(needsChannels ? { channels: nextChannels } : {}),
          },
        },
      },
      relationships: needsCustomerRel
        ? {
            ...session.relationships,
            customer: {
              ...session.relationships.customer,
              data: {
                ...session.relationships.customer.data,
                id: toCustomerId,
              },
            },
          }
        : session.relationships,
    };
  });

  if (changed) {
    updateStore('sessions', nextSessions);
  }
};

/**
 * Infer the pre-merge customer id from a session conversation channel name
 * when the event did not carry `fromCustomerId` (e.g. drift recovery).
 */
export const inferStalePubnubCustomerIdFromSessions = (
  survivorCustomerId: string,
): string | null => {
  const { sessions } = getStore();
  if (!sessions) {
    return null;
  }

  const sessionList = Object.keys(sessions).map((id) => sessions[id]);
  for (let i = 0; i < sessionList.length; i++) {
    const channels = sessionList[i]?.attributes?.pubnub?.data?.channels;
    if (channels) {
      const conversationChannel = channels.find(
        (channel) => channel.type === 'conversation',
      );
      const match = conversationChannel?.name?.match(/\.cust-([a-f0-9]{24})\./);
      if (match && match[1] && match[1] !== survivorCustomerId) {
        return match[1];
      }
    }
  }

  return null;
};
